import { Chart, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import VueSanitize from 'vue-sanitize';

import 'chartjs-adapter-date-fns';
// VCL Dependencies
import DataStoreFactory from './src/utilities/common/data-store';
import ApiFactory from './src/utilities/common/api';
import LogonFactory from './src/pages/vp-authentication/persistent-logon';
import SsoFactory from './src/utilities/common/sso';

// VCL plugin
import vRoles from './src/utilities/plugin/v-roles/v-roles';

// Components
import VpAccordion from './src/components/vp-accordion/VpAccordion.vue';
import VpAccordionGroup from './src/components/vp-accordion/VpAccordionGroup.vue';
import VpBusy from './src/components/vp-busy/VpBusy.vue';
import VpButton from './src/components/vp-button/VpButton.vue';
import VpCheckbox from './src/components/vp-checkbox/VpCheckbox.vue';
import VpIcon from './src/components/vp-icon/VpIcon.vue';
import VpSvgSprite from './src/components/vp-icon/VpSvgSprite.vue';
import VpInput from './src/components/vp-input/VpInput.vue';
import VpJsonEditor from './src/components/vp-json-editor/VpJsonEditor.vue';
import VpKebabMenu from './src/components/vp-kebab-menu/VpKebabMenu.vue';
import VpRadioButtons from './src/components/vp-radio-button/VpRadioButtons.vue';
import VpScatterChart from './src/components/charts/vp-scatter-chart/VpScatterChart.vue';
import VpHistogram from './src/components/charts/vp-histogram/VpHistogram.vue';
import VpChartDatePicker from './src/components/charts/vp-chart-date-picker/VpChartDatePicker.vue';
import VpChartLegendDropdown from './src/components/charts/vp-chart-legend-dropdown/VpChartLegendDropdown.vue';
import VpSelect from './src/components/vp-select/VpSelect.vue';
import VpSliderToggle from './src/components/vp-slider-toggle/VpSliderToggle.vue';
import VpTabMenu from './src/components/vp-tab-menu/VpTabMenu.vue';
import VpTextarea from './src/components/vp-textarea/VpTextarea.vue';
import VpToast from './src/components/vp-toast/VpToast.vue';
import VpTooltip from './src/components/vp-tooltip/VpTooltip.vue';
import InvalidEmailTooltip from './src/components/vp-tooltip/InvalidEmailTooltip.vue';
import SpamComplaintEmailTooltip from './src/components/vp-tooltip/SpamComplaintEmailTooltip.vue';
import VpWidget from './src/components/vp-widget/VpWidget.vue';
import VpPill from './src/components/vp-pill/VpPill.vue'
import VpStatus from './src/components/vp-status/VpStatus.vue';
/** Message Components */
import VpMessageDrafting from './src/components/vp-message/VpMessageDrafting.vue';
import VpMessageTemplate from './src/components/vp-message/VpMessageTemplate.vue';
import VpMessageInserts from './src/components/vp-message/VpMessageInserts.vue';
/** Modals */
import VpModal from './src/components/vp-modal/VpModal.vue';
import VpModalRoot from './src/components/vp-modal/VpModalRoot.vue';
import VpChangelogModal from './src/components/vp-changelog/VpChangelogModal.vue';
import VpConfirmationModal from './src/components/vp-confirmation/VpConfirmationModal.vue';

/** Table Components */
import VpTable from './src/components/vp-table/VpTable.vue';
import VpTableTools from './src/components/vp-table/VpTableTools.vue';
import VpTableTooltip from './src/components/vp-table/VpTableTooltip.vue';

// Pages
import VpAuthentication from './src/pages/vp-authentication/VpAuthentication.vue';

/** Picker Components */
import VpDatePicker from './src/components/vp-date-picker/VpDatePicker.vue';
import VpDayPicker from './src/components/vp-day-picker/VpDayPicker.vue';
import VpTimePicker from './src/components/vp-time-picker/VpTimePicker.vue';

/** Utility/Addon Components */
import VpTransition from './src/components/transition/VpTransition.vue';

const vcl = {
  install(Vue, options = {}) {
    const { store, config = {} } = options;
    if (!store) {
      throw new Error('Please initialize the VCL with a Vuex store');
    }

    const { app = 'utility', apiHost = 'http://localhost:3000', pid = null, ssoReturnToUrl } = config;

    Vue.prototype.app = app;

    // VCL dependencies
    const dataStore = DataStoreFactory();
    const api = ApiFactory({ dataStore }, { apiHost });
    const sso = SsoFactory({ api, dataStore }, { apiHost, ssoReturnToUrl });
    const logon = LogonFactory({ api, dataStore, sso }, { app, pid });

    Vue.prototype.$deps = {
      dataStore,
      api,
      sso,
      logon,
    };

    Chart.register(...registerables);
    Chart.register(zoomPlugin);

    Vue.use(VueSanitize, VueSanitize.defaults);

    // VCL components
    Vue.component('VpAccordion', VpAccordion);
    Vue.component('VpAccordionGroup', VpAccordionGroup);
    Vue.component('VpBusy', VpBusy);
    Vue.component('VpButton', VpButton);
    Vue.component('VpCheckbox', VpCheckbox);
    Vue.component('VpIcon', VpIcon);
    Vue.component('VpInput', VpInput);
    Vue.component('VpKebabMenu', VpKebabMenu);
    Vue.component('VpAuthentication', VpAuthentication);
    Vue.component('VpRadioButtons', VpRadioButtons);
    Vue.component('VpScatterChart', VpScatterChart);
    Vue.component('VpHistogram', VpHistogram);
    Vue.component('VpChartDatePicker', VpChartDatePicker);
    Vue.component('VpChartLegendDropdown', VpChartLegendDropdown);
    Vue.component('VpSelect', VpSelect);
    Vue.component('VpSliderToggle', VpSliderToggle);
    Vue.component('VpSvgSprite', VpSvgSprite);
    Vue.component('VpTabMenu', VpTabMenu);
    Vue.component('VpTextarea', VpTextarea);
    Vue.component('VpToast', VpToast);
    Vue.component('VpTooltip', VpTooltip);
    Vue.component('InvalidEmailTooltip', InvalidEmailTooltip);
    Vue.component('SpamComplaintEmailTooltip', SpamComplaintEmailTooltip);
    Vue.component('VpWidget', VpWidget);
    Vue.component('VpPill', VpPill);
    Vue.component('VpStatus', VpStatus);
    /** Message Components */
    Vue.component('VpMessageDrafting', VpMessageDrafting);
    Vue.component('VpMessageTemplate', VpMessageTemplate);
    Vue.component('VpMessageInserts', VpMessageInserts);
    /** Modals */
    Vue.component('VpModal', VpModal);
    Vue.component('VpModalRoot', VpModalRoot);
    Vue.component('VpChangelogModal', VpChangelogModal);
    Vue.component('VpConfirmationModal', VpConfirmationModal);
    /** Table Components */
    Vue.component('VpTable', VpTable);
    Vue.component('VpTableTools', VpTableTools);
    Vue.component('VpTableTooltip', VpTableTooltip);
    /** Picker Components */
    Vue.component('VpDatePicker', VpDatePicker);
    Vue.component('VpDayPicker', VpDayPicker);
    Vue.component('VpTimePicker', VpTimePicker);
    /** Utility/Addon Components */
    Vue.component('VpTransition', VpTransition);
  },
};

export {
  vcl as default,
  VpAccordion,
  VpAccordionGroup,
  VpBusy,
  VpButton,
  VpCheckbox,
  VpIcon,
  VpInput,
  VpJsonEditor,
  VpKebabMenu,
  VpRadioButtons,
  VpScatterChart,
  VpHistogram,
  VpChartDatePicker,
  VpChartLegendDropdown,
  VpSelect,
  VpSliderToggle,
  VpSvgSprite,
  VpTabMenu,
  VpTextarea,
  VpToast,
  VpTooltip,
  InvalidEmailTooltip,
  SpamComplaintEmailTooltip,
  VpWidget,
  vRoles,
  VpPill,
  VpStatus,
  /** Message Components */
  VpMessageDrafting,
  VpMessageTemplate,
  VpMessageInserts,
  /** Modals */
  VpModal,
  VpModalRoot,
  VpChangelogModal,
  VpConfirmationModal,
  /** Table Components */
  VpTable,
  VpTableTools,
  VpTableTooltip,
  /** Picker Components */
  VpDatePicker,
  VpDayPicker,
  VpTimePicker,
  /** Utility/Addon Components */
  VpTransition,
  /** Dependencies */
  DataStoreFactory,
  ApiFactory,
};
